import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs';
import { filterNil } from '@ngneat/elf';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AnalyticsEventType } from 'src/app/models/analytics';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/services/user.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { EventCategory, SessionEvent } from '../SessionEvent';
import { RaiseHandRepositoryService } from './raise-hand-repository.service';

@Injectable()
export class RaiseHandButtonStateService {
  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    private raiseHandRepositoryService: RaiseHandRepositoryService,
    private userService: UserService,
    private sessionSharedDataService: SessionSharedDataService,
    private featureManagementService: FeatureManagementService,
  ) {}

  toggleRaiseHand(): void {
    if (!this.featureManagementService.isRaiseHandEnabled()) {
      return;
    }

    const userId = this.userService.userId;
    if (!userId) {
      return;
    }
    const currentValue = this.raiseHandRepositoryService.getUserRaiseHandCurrentValue(userId);
    const nextValue = !currentValue;

    // the event is being sent for compatibility with the old code. we can remove it after a few weeks.
    const event = new SessionEvent(EventCategory.Users, 'users:raise-hand', nextValue);
    this.sessionSharedDataService.sessionEventController?.sendEvent(this, event);

    this.realtimeSpaceService.service.modifyTemporaryUserMetadata({
      raiseHand: nextValue,
    });
    if (nextValue) {
      this.analyticsService.addToAnalyticsEventsBatch(AnalyticsEventType.HAND_RAISED);
    }
  }

  clearRaiseHand(): void {
    if (this.getUserRaiseHandCurrentValue()) {
      this.toggleRaiseHand();
    }
  }

  getUserRaiseHandCurrentValue(): boolean {
    const userId = this.userService.userId;
    if (!userId) {
      return false;
    }
    return this.raiseHandRepositoryService.getUserRaiseHandCurrentValue(userId);
  }

  get isHandRaised$() {
    return this.userService.user.pipe(
      filterNil(),
      map((user) => user.user._id),
      switchMap((userId: string) => this.raiseHandRepositoryService.getUserRaiseHand$(userId)),
    );
  }

  label$ = this.isHandRaised$.pipe(
    map((isHandRaised): string =>
      isHandRaised
        ? this.translateService.instant('Lower Hand')
        : this.translateService.instant('Raise Hand'),
    ),
  );
}
